/**
 * Support work across every customer, and the recurring tickets that open some of it.
 *
 * The port of `Views/TicketsPage.xaml` + `TicketsViewModel`. One customer filter narrows BOTH halves
 * of the page - the list and the recurring section below it - and needs no Apply button, as the
 * combo did there. The recurring section is the same builder the customer page hosts; a template
 * that opens tickets refreshes this list through its `onChanged`, and nothing else on the page.
 *
 * Status, priority and the search box narrow the list in the browser (see `ticketFilter.ts`); they
 * do not touch the recurring section, which has no status or priority of its own to match.
 *
 * `?customer=<id>` preselects the filter, so a link from elsewhere can land on one customer's work.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import * as hub from '../api/hub.js';
import { describeError } from '../api/http.js';
import { TicketPriority, type TicketDto } from '../contracts.gen.js';
import { plural } from '../format.js';
import { CardList } from '../ui/CardList.js';
import { EmptyState } from '../ui/EmptyState.js';
import { StatusLine } from '../ui/StatusLine.js';
import { isFiltering, STATUS_FILTERS, ticketMatches, type TicketFilter } from '../ticketFilter.js';
import {
  button,
  buttonRow,
  card,
  comboBox,
  field,
  fillWith,
  GAP,
  h1,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  textBox,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';
import { PRIORITY_NAMES } from './sections/recurring.js';
import { loadCustomerChoices, type CustomerChoices } from './sections/recurring.js';
import { recurringTicketsSection } from './sections/recurring-tickets.js';
import { ticketCard } from './sections/ticket-card.js';

/** The OpenSilver list rendered everything; a cap with a visible count is the rule every list here follows. */
const MAX_ROWS = 250;

export function ticketsPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      let choices: CustomerChoices = { names: [], ids: [] };
      const names = new Map<number, string>();
      /** Setting the picker's items during load fires its selection event; nothing listens until then. */
      let ready = false;

      const wanted = Number(ctx.query.get('customer'));
      const queryCustomer = Number.isInteger(wanted) && wanted > 0 ? wanted : undefined;

      const status = new StatusLine();
      const countLine = muted('');

      // Index 0 is "Any customer"; the rest line up with `choices.ids`.
      const customerFilter = comboBox<string>(['Any customer'], {
        selectedIndex: 0,
        onSelectionChanged: () => void applyFilter()
      });
      const statusFilter = comboBox<string>(
        STATUS_FILTERS.map(s => s.label),
        { selectedIndex: 0, onSelectionChanged: () => render() }
      );
      // Index 0 is "Any priority"; index n is priority n - 1, which is what PRIORITY_NAMES is keyed by.
      const priorityFilter = comboBox<string>(['Any priority', ...PRIORITY_NAMES], {
        selectedIndex: 0,
        onSelectionChanged: () => render()
      });
      const search = textBox({ placeholder: 'Number, title, customer or contact' });
      search.on('textchanged', () => render());

      const header = splitRow({ align: 'end' });
      fillWith(
        header,
        stackOf(vstack({ spacing: GAP.xs }), h1('Tickets'), muted('Support work across all customers.'))
      );

      const filters = card();
      filters.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          stackOf(
            buttonRow({ spacing: GAP.md, align: 'end' }),
            field('Customer', customerFilter, { width: 220 }),
            field('Status', statusFilter, { width: 190 }),
            field('Priority', priorityFilter, { width: 130 }),
            field('Search', search, { width: 260 })
          ),
          stackOf(buttonRow(), button('Reset', () => reset()))
        )
      );

      let tickets: TicketDto[] = [];

      const list = new CardList<TicketDto>({
        key: t => t.id,
        max: MAX_ROWS,
        build: t =>
          ticketCard(t, {
            customerName: selectedCustomerId() === undefined ? names.get(t.customerId) : undefined,
            onOpen: ticket => ctx.go(`/tickets/${ticket.id}`)
          }),
        // The customer line depends on the filter, so a row is rebuilt when the filter moves.
        version: t => [t, selectedCustomerId() === undefined]
      });
      const empty = new EmptyState('No tickets.');

      const recurring = recurringTicketsSection({
        ctx,
        customerFilterId: queryCustomer,
        onChanged: () => void refreshTickets()
      });

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(vstack({ spacing: GAP.md }), header, status.label, filters, countLine, empty.panel, list.panel)
      );
      content.controls.add(recurring.panel);
      host.controls.add(view);

      function selectedCustomerId(): number | undefined {
        const index = customerFilter.selectedIndex;
        return index > 0 && index - 1 < choices.ids.length ? choices.ids[index - 1] : undefined;
      }

      async function applyFilter(): Promise<void> {
        if (!ready) return;
        const id = selectedCustomerId();
        await Promise.all([refreshTickets(), recurring.setCustomerFilter(id)]);
      }

      function currentFilter(): TicketFilter {
        const p = priorityFilter.selectedIndex;
        return {
          status: STATUS_FILTERS[Math.max(0, statusFilter.selectedIndex)]?.value ?? 'any',
          priority: p > 0 ? ((p - 1) as TicketPriority) : 'any',
          search: search.text
        };
      }

      /** Re-applies the status, priority and search boxes to the list already loaded. */
      function render(): void {
        if (!ready) return;
        const customerId = selectedCustomerId();
        const filter = currentFilter();
        const shown = tickets.filter(t => ticketMatches(t, filter, names.get(t.customerId)));
        list.set(shown);
        const filtering = isFiltering(filter);
        empty.message =
          tickets.length > 0 && filtering
            ? 'No tickets match these filters.'
            : customerId === undefined
              ? 'No tickets.'
              : 'No tickets for this customer.';
        empty.update(shown.length);
        countLine.text = countLabel(shown.length, tickets.length, filtering, customerId !== undefined, list.overflow);
        relayout(content);
      }

      function reset(): void {
        ready = false;
        statusFilter.selectedIndex = 0;
        priorityFilter.selectedIndex = 0;
        search.text = '';
        const customerChanged = customerFilter.selectedIndex !== 0;
        customerFilter.selectedIndex = 0;
        ready = true;
        if (customerChanged) void applyFilter();
        else render();
      }

      async function refreshTickets(): Promise<void> {
        const customerId = selectedCustomerId();
        try {
          const loaded = await crm.getTickets(customerId, ctx.dispose.signal());
          if (ctx.cancelled() || customerId !== selectedCustomerId()) return;
          tickets = loaded;
          render();
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the tickets: ' + describeError(error));
        }
      }

      async function initialize(): Promise<void> {
        // The list still works unfiltered when the customers cannot be read.
        choices = await loadCustomerChoices(ctx.dispose.signal());
        if (ctx.cancelled()) return;
        choices.ids.forEach((id, i) => names.set(id, choices.names[i] ?? ''));
        customerFilter.items = ['Any customer', ...choices.names];

        const at = queryCustomer === undefined ? -1 : choices.ids.indexOf(queryCustomer);
        customerFilter.selectedIndex = at >= 0 ? at + 1 : 0;
        ready = true;

        await Promise.all([refreshTickets(), recurring.initialize()]);
        // An id the picker does not know (archived, or a typo) falls back to every customer.
        if (queryCustomer !== undefined && at < 0) await recurring.setCustomerFilter(undefined);
      }

      // A ticket raised from the agent tray lands in the list while it is open. The keyed list keeps
      // every existing row, so this does not disturb anything being read.
      ctx.dispose.add(
        hub.on('TicketCreated', () => {
          if (ready) void refreshTickets();
        })
      );

      void initialize();
    }
  };
}

function countLabel(count: number, total: number, filtering: boolean, oneCustomer: boolean, overflow: number): string {
  if (count === 0) return '';
  const scope = oneCustomer ? 'for this customer' : 'across all customers';
  const head = filtering
    ? `${count} of ${plural(total, 'ticket')} ${scope} match`
    : `${plural(count, 'ticket')} ${scope}`;
  return overflow > 0 ? `${head} - showing the most recently updated ${count - overflow}.` : `${head}.`;
}
